import React from 'react';
import Section from './Section';
import { BadgeJapaneseYen, Clock, MessageCircle, ArrowRight, X, Check } from 'lucide-react';
import { useInView } from 'react-intersection-observer';

const Technology: React.FC = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  const comparisons = [
    {
      icon: BadgeJapaneseYen,
      label: '費用',
      traditional: '数百万〜数千万円',
      senrigan: '10万〜300万円（60〜80%OFF）',
    },
    {
      icon: Clock,
      label: '期間',
      traditional: '6ヶ月〜1年',
      senrigan: '1週間〜3ヶ月',
    },
    {
      icon: MessageCircle,
      label: 'やり取り',
      traditional: '営業→PM→SE→PG\n伝言ゲーム',
      senrigan: '開発者に直接相談\nムダなやり取りゼロ',
    }
  ];

  return (
    <Section id="why" className="bg-white">
      {/* フルワイド画像 */}
      <div className="relative h-[180px] sm:h-[260px] lg:h-[340px] overflow-hidden">
        <img
          src="/images/technology.webp"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white" />
      </div>

      <div ref={ref} className={`mx-auto max-w-6xl px-5 lg:px-6 pb-20 pt-8 lg:pb-28 lg:pt-12 transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#B8860B]/30 bg-[#B8860B]/10 px-4 py-1.5 mb-6">
            <span className="text-sm font-semibold text-[#B8860B]">選ばれる理由</span>
          </div>
          <h2 className="text-3xl font-bold lg:text-4xl leading-tight text-[#2D3748]">
            従来の開発会社との<br />
            <span className="text-gradient">3つの違い</span>
          </h2>
        </div>

        {/* 比較テーブル */}
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden shadow-sm mb-10 max-w-4xl mx-auto">
          {/* ヘッダー行 */}
          <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] bg-[#F5F3EF]">
            <div className="px-3 sm:px-6 py-4 text-center min-w-0">
              <span className="text-sm lg:text-base font-bold tracking-wide text-[#9B4D3A]">一般的な開発会社</span>
            </div>
            <div className="w-14 lg:w-16" />
            <div className="px-3 sm:px-6 py-4 text-center min-w-0">
              <span className="text-sm lg:text-base font-bold tracking-wide text-[#B8860B]">Senrigan</span>
            </div>
          </div>

          {/* 比較行 */}
          {comparisons.map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={index} className={`grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] ${index < comparisons.length - 1 ? 'border-b border-[#E8E6E1]' : ''}`}>
                {/* 従来 */}
                <div className="px-3 sm:px-6 py-6 lg:py-7 bg-[#FAFAF8] flex flex-col items-center justify-center text-center min-w-0">
                  <div className="flex items-center justify-center gap-1.5 mb-2">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#9B4D3A]/15">
                      <X className="h-3.5 w-3.5 text-[#9B4D3A]" />
                    </div>
                  </div>
                  <div className="text-[#9B4D3A] font-medium line-through whitespace-pre-line text-sm sm:text-base lg:text-xl">{item.traditional}</div>
                </div>

                {/* 中央ラベル + 矢印 */}
                <div className="w-14 lg:w-16 flex flex-col items-center justify-center bg-white border-x border-[#E8E6E1] gap-1.5">
                  <div className="flex h-9 w-9 lg:h-10 lg:w-10 shrink-0 items-center justify-center rounded-full bg-[#1B2A4A]">
                    <Icon className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
                  </div>
                  <span className="text-[10px] lg:text-xs font-bold text-[#7B8794] tracking-wider">{item.label}</span>
                  <ArrowRight className="h-5 w-5 text-[#B8860B]" />
                </div>

                {/* Senrigan */}
                <div className="px-3 sm:px-6 py-6 lg:py-7 bg-white flex flex-col items-center justify-center text-center min-w-0">
                  <div className="flex items-center justify-center gap-1.5 mb-2">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2D8659]/15">
                      <Check className="h-3.5 w-3.5 text-[#2D8659]" />
                    </div>
                  </div>
                  <div className="text-[#1B2A4A] font-bold whitespace-pre-line text-sm sm:text-base lg:text-xl">{item.senrigan}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 説明カード */}
        <div className="rounded-2xl bg-[#1B2A4A] p-8 lg:p-10 text-[#F5F3EF]">
          <h3 className="text-xl font-bold mb-4 text-[#F5F3EF]">AI × 1人開発だから実現できる</h3>
          <p className="text-[#F5F3EF]/80 leading-relaxed mb-6 text-justify">
            大手の開発会社は、営業、プロジェクトマネージャー、設計者、開発者…何人もの人件費が見積もりに乗ります。
            Senriganは、代表が直接ヒアリングし、AIを活用して1人で開発。だから速くて安い。
          </p>
          <a href="#contact" className="inline-flex items-center gap-2 rounded-lg bg-[#B8860B] px-6 py-3 text-white font-semibold hover:bg-[#A0750A] transition-colors shadow-lg shadow-[#B8860B]/20">
            無料で相談する
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </Section>
  );
};

export default Technology;
